import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Search, Briefcase, Users, Building2, Sparkles, ChevronRight, CheckCircle2,
  Zap, ShieldCheck, HeartHandshake, LineChart, Target, Eye, Compass, Star, Quote,
  UserPlus, FileSearch, MessagesSquare, Award, Loader2,
} from "lucide-react";
import StateCitySelect from "@/components/StateCitySelect";
import { LandingPageHeader } from "@/components/LandingPageHeader";
import { LandingPageFooter } from "@/components/LandingPageFooter";
import { useAuth } from "@/contexts/AuthContext";
import { showSuccess } from "@/utils/toast";
import { trackPixelEvent } from "@/utils/pixel";
import { cn } from "@/lib/utils";

const metrics = [
  { value: "+2 Mil", label: "Vagas publicadas ativamente" },
  { value: "+100", label: "Empresas parceiras recrutando" },
  { value: "+50 Mil", label: "Profissionais contratados" },
];

const howItWorks = [
  { step: "01", icon: UserPlus, title: "Crie seu perfil", text: "Candidato ou empresa: o cadastro leva poucos minutos e já deixa você pronto para agir." },
  { step: "02", icon: FileSearch, title: "Publique ou encontre", text: "Empresas publicam a vaga com os requisitos reais. Candidatos encontram oportunidades que combinam com o perfil deles." },
  { step: "03", icon: MessagesSquare, title: "Conecte-se direto", text: "Chat integrado e acompanhamento de status — sem intermediários, sem ligação perdida." },
  { step: "04", icon: Award, title: "Feche o processo", text: "Contratação mais rápida para a empresa, resposta mais rápida para o candidato." },
];

const differentiators = [
  { icon: Zap, title: "Rapidez na divulgação", text: "Sua vaga vai ao ar e já começa a alcançar candidatos compatíveis no mesmo dia." },
  { icon: ShieldCheck, title: "Qualidade na seleção", text: "Triagem por teste comportamental configurável — só avança quem realmente combina com a vaga." },
  { icon: HeartHandshake, title: "Atendimento próximo", text: "Suporte humano de verdade, para empresas e candidatos, do cadastro à contratação." },
  { icon: LineChart, title: "Estratégia digital", text: "Divulgação orientada por dados para colocar a vaga certa na frente da pessoa certa." },
];

const testimonials = [
  {
    name: "Ricardo Santos",
    role: "Analista de Sistemas",
    text: "Encontrei uma vaga compatível com meu perfil em poucos dias e o processo de candidatura foi direto, sem burocracia.",
    avatar: "/ricardo_santos.webp",
  },
  {
    name: "Juliana Lima",
    role: "Gerente de Marketing",
    text: "O que mais gostei foi acompanhar cada etapa da candidatura em tempo real, sem precisar ficar mandando e-mail para saber o status.",
    avatar: "/juliana_lima.webp",
  },
  {
    name: "Fernanda Costa",
    role: "RH · TechNova Soluções",
    text: "Reduzimos o tempo de triagem com o teste comportamental configurável — só chega para entrevista quem já bate com o perfil que buscamos.",
    avatar: "/company-logo-placeholder.svg",
    isCompany: true,
  },
];

const values = ["Compromisso com resultados", "Transparência", "Agilidade", "Valorização das pessoas", "Inovação constante"];

const contactReasons = [
  { value: "candidato", label: "Sou candidato" },
  { value: "empresa", label: "Sou empresa" },
  { value: "outro", label: "Outro assunto" },
];

// Título de seção reutilizado em toda a página — mantém hierarquia e
// espaçamento consistentes entre as seções.
const SectionHeading = ({ eyebrow, title, desc, light = false }: { eyebrow: string; title: string; desc?: string; light?: boolean }) => (
  <div className="text-center mb-12 md:mb-16">
    <span className={cn("inline-block text-xs font-bold uppercase tracking-widest mb-3", light ? "text-gps-yellow" : "text-gps-medium-blue")}>
      {eyebrow}
    </span>
    <h2 className={cn("text-3xl md:text-4xl font-extrabold tracking-tight text-balance", light ? "text-white" : "text-gps-dark-blue")}>
      {title}
    </h2>
    {desc && (
      <p className={cn("text-lg font-normal mt-3 max-w-2xl mx-auto text-balance", light ? "text-white/70" : "text-gray-600")}>
        {desc}
      </p>
    )}
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [cityFilter, setCityFilter] = useState<string | undefined>(undefined);
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined);
  const { session } = useAuth();

  const [contactForm, setContactForm] = useState({ name: "", email: "", reason: "candidato", message: "" });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  // Evento: usuário visualizou a landing page
  useEffect(() => {
    trackPixelEvent('ViewContent', {
      content_name: 'Landing Page',
      content_category: 'Homepage',
    });
  }, []);

  const handleSearch = () => {
    if (searchTerm || cityFilter || stateFilter) {
      trackPixelEvent('Search', {
        search_string: searchTerm || '',
        content_category: 'LandingPage',
        city: cityFilter || '',
        state: stateFilter || '',
      });
    }
    const queryParams = new URLSearchParams();
    if (searchTerm) queryParams.append("searchTerm", searchTerm);
    if (cityFilter) queryParams.append("cityFilter", cityFilter);
    if (stateFilter) queryParams.append("stateFilter", stateFilter);
    navigate(`/candidato/vagas?${queryParams.toString()}`);
  };

  // Sem backend de contato no projeto ainda (Contact.tsx também só dispara o
  // evento de pixel) — mantém o mesmo comportamento honesto aqui em vez de
  // simular um envio que não existe.
  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    trackPixelEvent('Contact', { content_category: contactForm.reason });
    setTimeout(() => {
      setIsSubmittingContact(false);
      showSuccess("Mensagem enviada! Nosso time entrará em contato em breve.");
      setContactForm({ name: "", email: "", reason: "candidato", message: "" });
    }, 600);
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "GPS Emprega",
    "url": "https://www.gpsemprega.com.br",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.gpsemprega.com.br/logo-fundo-branco.png",
      "width": 200,
      "height": 60
    },
    "description": "Plataforma de recrutamento e seleção que conecta candidatos talentosos com as melhores empresas do Brasil.",
    "foundingDate": "2024",
    "areaServed": "BR",
    "serviceType": "Recrutamento e Seleção",
    "sameAs": [
      "https://www.instagram.com/gpsemprega",
      "https://www.linkedin.com/company/gpsemprega",
      "https://www.facebook.com/gpsemprega"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "availableLanguage": "Portuguese",
      "url": "https://www.gpsemprega.com.br/contato"
    }
  };

  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "GPS Emprega",
    "url": "https://www.gpsemprega.com.br",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.gpsemprega.com.br/candidato/vagas?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
    <SEO
      title="Vagas de Emprego em Todo o Brasil – Cadastre seu Currículo Grátis"
      description="Encontre vagas de emprego em todo o Brasil. Conectamos candidatos a empresas que estão contratando agora. Cadastre seu currículo grátis e seja encontrado por recrutadores!"
      canonical="/"
      keywords="vagas de emprego, emprego, trabalho, vagas de trabalho, currículo grátis, recrutamento, empregos no Brasil, oportunidades de emprego, cadastrar curriculo, recolocacao profissional, vagas home office"
      jsonLd={[organizationSchema, webSiteSchema]}
    />
    <div className="flex flex-col min-h-screen bg-white text-gray-900 font-sans tracking-normal overflow-x-hidden">
      <LandingPageHeader transparent={false} />
      <main className="flex-grow">

        {/* ── HERO ── */}
        <section className="relative bg-gradient-to-b from-[#DCE6F5] via-[#EAF1FA] to-white py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-[-10%] right-[-8%] w-[480px] h-[480px] bg-gps-medium-blue/10 rounded-full blur-[140px]" />
            <div className="absolute bottom-[-15%] left-[-10%] w-[360px] h-[360px] bg-gps-light-blue/8 rounded-full blur-[120px]" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
            <Badge variant="outline" className="mb-8 border-gps-medium-blue/20 bg-white text-gps-medium-blue px-4 py-1.5 rounded-full shadow-sm">
              <Sparkles className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Recrutamento que conecta de verdade
            </Badge>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-[1.10] tracking-tight text-gps-dark-blue text-balance">
              Conectamos talentos a <span className="text-gps-medium-blue">oportunidades reais</span>
            </h1>

            <p className="text-lg md:text-xl mb-12 max-w-2xl mx-auto text-gray-800 leading-relaxed font-medium">
              A plataforma que aproxima empresas de quem elas precisam contratar e candidatos da vaga que eles realmente querem — com agilidade, transparência e um processo que qualquer pessoa entende.
            </p>

            {/* Dois CTAs — empresas e candidatos */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Button size="lg" className="w-full sm:w-auto bg-gps-yellow hover:bg-yellow-400 text-gps-dark-blue h-14 px-8 rounded-2xl font-bold text-base shadow-lg shadow-gps-yellow/20 group" asChild>
                <Link to="/cadastro/empresa">
                  <Building2 className="w-5 h-5 mr-2" aria-hidden="true" />
                  Quero contratar
                  <ChevronRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-gps-dark-blue text-gps-dark-blue hover:bg-gps-dark-blue hover:text-white h-14 px-8 rounded-2xl font-bold text-base group" asChild>
                <Link to="/cadastro/candidato">
                  <Users className="w-5 h-5 mr-2" aria-hidden="true" />
                  Quero uma vaga
                  <ChevronRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                </Link>
              </Button>
            </div>

            {/* Busca rápida de vagas */}
            <div className="max-w-3xl mx-auto p-2.5 bg-white border border-gray-200 rounded-3xl shadow-[0_12px_40px_-12px_rgba(20,33,61,0.15)]">
              <div className="grid gap-2 grid-cols-1 md:grid-cols-[1.4fr_1fr_auto] items-center">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" aria-hidden="true" />
                  <label htmlFor="hero-search-term" className="sr-only">Cargo ou empresa</label>
                  <Input
                    id="hero-search-term"
                    type="text"
                    placeholder="Ou busque uma vaga agora: cargo, empresa..."
                    className="w-full border-none text-gps-dark-blue placeholder:text-gray-400 h-14 pl-12 rounded-2xl focus-visible:ring-2 focus-visible:ring-gps-medium-blue/30 text-base"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <div className="md:border-l md:border-gray-100 md:pl-3">
                  <StateCitySelect
                    selectedState={stateFilter}
                    onStateChange={setStateFilter}
                    selectedCity={cityFilter}
                    onCityChange={setCityFilter}
                    hideLabels={true}
                    statePlaceholder="Estado (UF)"
                    cityPlaceholder="Qual Cidade?"
                  />
                </div>
                <Button size="lg" className="w-full md:w-auto bg-gps-medium-blue hover:bg-gps-dark-blue text-white h-14 px-8 rounded-2xl font-bold text-base" onClick={handleSearch}>
                  Buscar
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── COMO FUNCIONA ── */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeading eyebrow="Simples assim" title="Como funciona" desc="Do cadastro à contratação, em quatro passos claros." />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {howItWorks.map((item, i) => (
                <div key={item.step} className="relative bg-[#FAFBFF] rounded-3xl border border-gray-100 p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                  <span className="text-xs font-bold text-gps-medium-blue/50 tracking-widest">{item.step}</span>
                  <div className="w-11 h-11 rounded-xl bg-gps-medium-blue/10 flex items-center justify-center my-4">
                    <item.icon className="h-5 w-5 text-gps-medium-blue" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-gps-dark-blue mb-1.5">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                  {i < howItWorks.length - 1 && (
                    <ChevronRight className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 w-5 h-5 text-gray-200" aria-hidden="true" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── DIFERENCIAIS ── */}
        <section className="py-16 md:py-24 bg-[#FAFBFF] border-y border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeading eyebrow="Por que a GPS Emprega" title="Feito para quem não quer perder tempo" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {differentiators.map((d) => (
                <div key={d.title} className="bg-white rounded-3xl border border-gray-100 p-6">
                  <div className="w-11 h-11 rounded-xl bg-gps-medium-blue/10 flex items-center justify-center mb-4">
                    <d.icon className="h-5 w-5 text-gps-medium-blue" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-gps-dark-blue mb-1.5">{d.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{d.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── PARA EMPRESAS / PARA CANDIDATOS ── */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeading eyebrow="Dois lados, uma plataforma" title="Feito sob medida para você" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-white rounded-3xl border-2 border-gray-100 p-8 md:p-10 flex flex-col hover:border-gps-medium-blue/30 transition-colors duration-300">
                <div className="w-12 h-12 rounded-2xl bg-gps-medium-blue/10 flex items-center justify-center mb-5">
                  <Building2 className="h-6 w-6 text-gps-medium-blue" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-extrabold text-gps-dark-blue mb-4">Para Empresas</h3>
                <ul className="space-y-3 mb-8 flex-1">
                  {[
                    "Publique vagas com requisitos reais em minutos",
                    "Teste comportamental configurável por vaga",
                    "Kanban para acompanhar todo o processo seletivo",
                    "Chat direto com os candidatos, sem intermediários",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-5 h-5 text-gps-medium-blue shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button size="lg" className="bg-gps-medium-blue hover:bg-gps-dark-blue text-white rounded-2xl h-13 font-bold" asChild>
                  <Link to="/cadastro/empresa">
                    Anunciar uma vaga <ChevronRight className="w-4 h-4 ml-1.5" aria-hidden="true" />
                  </Link>
                </Button>
              </div>

              <div className="bg-gps-dark-blue rounded-3xl p-8 md:p-10 flex flex-col text-white">
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
                  <Users className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-extrabold mb-4">Para Candidatos</h3>
                <ul className="space-y-3 mb-8 flex-1">
                  {[
                    "Currículo uma vez, candidaturas ilimitadas",
                    "Alertas automáticos de vagas compatíveis",
                    "Acompanhe o status da candidatura em tempo real",
                    "Cadastro e candidatura 100% gratuitos",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-5 h-5 text-gps-yellow shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-white/85">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button size="lg" className="bg-white hover:bg-gray-100 text-gps-dark-blue rounded-2xl h-13 font-bold" asChild>
                  <Link to="/cadastro/candidato">
                    Cadastrar meu currículo <ChevronRight className="w-4 h-4 ml-1.5" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── PROVA SOCIAL ── */}
        <section className="py-16 md:py-24 bg-[#FAFBFF] border-y border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <SectionHeading eyebrow="Resultado, não promessa" title="Quem usa, recomenda" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto mb-14">
              {metrics.map((metric) => (
                <div key={metric.label} className="text-center p-6 rounded-2xl border border-gray-100 bg-white">
                  <p className="text-3xl font-extrabold text-gps-dark-blue mb-1 leading-none tracking-tight tabular-nums">{metric.value}</p>
                  <p className="text-sm text-gray-600 font-medium">{metric.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {testimonials.map((t) => (
                <figure key={t.name} className="bg-white rounded-3xl border border-gray-100 p-7 flex flex-col hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                  <Quote className="h-6 w-6 text-gps-medium-blue/40 mb-4" aria-hidden="true" />
                  <blockquote className="text-gray-700 leading-relaxed mb-6 flex-1">"{t.text}"</blockquote>
                  <figcaption className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt=""
                      width={160}
                      height={160}
                      className={cn("w-11 h-11 rounded-full object-cover border border-gray-100", t.isCompany && "object-contain p-1.5 bg-white")}
                      loading="lazy"
                    />
                    <div>
                      <p className="font-bold text-gps-dark-blue text-sm">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                    <div className="ml-auto flex gap-0.5" aria-hidden="true">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-gps-yellow text-gps-yellow" />)}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ── MISSÃO / VISÃO / VALORES ── */}
        <section className="py-16 md:py-24 bg-gps-dark-blue text-white relative overflow-hidden">
          <div className="absolute -top-32 -left-32 w-80 h-80 bg-gps-medium-blue rounded-full blur-[120px] opacity-30" aria-hidden="true" />
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl relative z-10">
            <SectionHeading eyebrow="O que nos move" title="Missão, visão e valores" light />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
                <Target className="h-7 w-7 text-gps-yellow mb-4" aria-hidden="true" />
                <h3 className="text-lg font-bold mb-2">Missão</h3>
                <p className="text-white/70 leading-relaxed">
                  Conectar pessoas a oportunidades de trabalho, promovendo crescimento profissional e desenvolvimento econômico.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
                <Eye className="h-7 w-7 text-gps-yellow mb-4" aria-hidden="true" />
                <h3 className="text-lg font-bold mb-2">Visão</h3>
                <p className="text-white/70 leading-relaxed">
                  Ser referência nacional em soluções de empregabilidade.
                </p>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <div className="flex items-center gap-2 mb-5">
                <Compass className="h-6 w-6 text-gps-yellow" aria-hidden="true" />
                <h3 className="text-lg font-bold">Valores</h3>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {values.map((v) => (
                  <span key={v} className="text-sm font-semibold bg-white/10 border border-white/10 rounded-full px-4 py-2">
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA FINAL + FORMULÁRIO DE CONTATO ── */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
              <div className="text-center lg:text-left">
                <h2 className="text-3xl md:text-4xl font-extrabold text-gps-dark-blue mb-4 tracking-tight text-balance">
                  Pronto para dar o próximo passo?
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Fale com a gente ou cadastre-se agora — leva menos de dois minutos, e você pode começar como candidato ou como empresa.
                </p>
                <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 justify-center lg:justify-start">
                  <Button size="lg" className="bg-gps-yellow hover:bg-yellow-400 text-gps-dark-blue rounded-2xl h-13 font-bold" asChild>
                    <Link to="/cadastro/empresa">Sou empresa</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="border-2 border-gps-dark-blue text-gps-dark-blue hover:bg-gps-dark-blue hover:text-white rounded-2xl h-13 font-bold" asChild>
                    <Link to="/cadastro/candidato">Sou candidato</Link>
                  </Button>
                </div>
              </div>

              <form onSubmit={handleContactSubmit} className="bg-[#FAFBFF] border border-gray-100 rounded-3xl p-6 md:p-8 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-name">Nome</Label>
                    <Input
                      id="contact-name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Seu nome"
                      className="h-11 rounded-xl bg-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-email">E-mail</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="seu@email.com"
                      className="h-11 rounded-xl bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Você é</Label>
                  <div className="flex flex-wrap gap-2">
                    {contactReasons.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setContactForm((f) => ({ ...f, reason: r.value }))}
                        className={cn(
                          "text-sm font-semibold px-4 py-2.5 rounded-xl border transition-colors min-h-[44px]",
                          contactForm.reason === r.value
                            ? "bg-gps-medium-blue text-white border-gps-medium-blue"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gps-medium-blue/40"
                        )}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="contact-message">Mensagem</Label>
                  <Textarea
                    id="contact-message"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder="Como podemos ajudar?"
                    className="min-h-[110px] rounded-xl bg-white resize-none"
                  />
                </div>

                <Button type="submit" size="lg" disabled={isSubmittingContact} className="w-full bg-gps-dark-blue hover:bg-[#0B152A] text-white rounded-2xl h-13 font-bold">
                  {isSubmittingContact && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
                  Enviar mensagem
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <LandingPageFooter />
    </div>
    </>
  );
};

export default LandingPage;
